import { useEffect, useState, useCallback, type RefObject } from 'react'
import { debounce } from 'lodash'
import { logger } from 'v2/utils/logger'

interface UseScrollBehaviorProps {
  scrollViewportRef: RefObject<HTMLDivElement>
  chunks?: unknown[]
  onReachingTop?: () => void
  onReachingBottom?: () => void
}

export function useScrollBehavior({
  scrollViewportRef,
  chunks,
  onReachingTop,
  onReachingBottom,
}: UseScrollBehaviorProps) {
  const [hasUserScrolled, setHasUserScrolled] = useState(false)
  const [isContentFullyLoaded, setIsContentFullyLoaded] = useState(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  const scrollToBottom = useCallback(
    (smooth = false) => {
      if (scrollViewportRef.current) {
        const scrollOptions: ScrollToOptions = {
          top: scrollViewportRef.current.scrollHeight,
          behavior: smooth ? 'smooth' : 'auto',
        }
        scrollViewportRef.current.scrollTo(scrollOptions)
      }
    },
    [scrollViewportRef],
  )

  const scrollToBottomSmooth = useCallback(() => {
    scrollToBottom(true)
  }, [scrollToBottom])

  useEffect(() => {
    if (!onReachingBottom && isInitialLoad && chunks && chunks.length > 0) {
      scrollToBottom(false) // Instant scroll on initial load
      setIsInitialLoad(false)
    } else if (!onReachingBottom && !hasUserScrolled && chunks && chunks.length > 0) {
      scrollToBottomSmooth() // Smooth scroll for subsequent updates
    }
  }, [
    chunks,
    hasUserScrolled,
    scrollToBottom,
    scrollToBottomSmooth,
    isInitialLoad,
    onReachingBottom,
  ])

  useEffect(() => {
    const handleScroll = debounce(() => {
      if (!scrollViewportRef.current) return

      const { scrollTop, scrollHeight, clientHeight } = scrollViewportRef.current
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10 // 10px threshold
      setHasUserScrolled(!isAtBottom)

      const contentDoesntFillViewport = scrollHeight <= clientHeight
      const isAtTop = scrollTop === 0

      if (isContentFullyLoaded) {
        if (contentDoesntFillViewport) {
          logger.log("Content doesn't fill viewport")
        } else if (isAtTop) {
          logger.log('Scrolled to top')
          onReachingTop?.()
        } else if (isAtBottom) {
          logger.log('Scrolled to bottom')
          onReachingBottom?.()
        }
      }
    }, 100)

    const scrollViewport = scrollViewportRef.current
    if (scrollViewport) scrollViewport.addEventListener('scroll', handleScroll)

    return () => {
      if (scrollViewport) scrollViewport.removeEventListener('scroll', handleScroll)
      handleScroll.cancel()
    }
  }, [isContentFullyLoaded, scrollViewportRef, onReachingTop, onReachingBottom])

  useEffect(() => {
    const timer = setTimeout(() => setIsContentFullyLoaded(true), 100)
    return () => clearTimeout(timer)
  }, [chunks])

  return { hasUserScrolled, scrollToBottom, scrollToBottomSmooth }
}
